import { format, isValid, parseISO, isSameDay } from 'date-fns';

const END_OF_DAYS = '9999-12-31T00:00:00';

/**
 * Formats a supplied date to the supplied dateFormat. Defaults to MM/dd/yyyy.
 *
 * @param {*} date - the date to be formatted
 * @param {*} dateFormat - the format to be applied
 * @param {*} truncateTimezone - defaults to true, will truncate the timezone from the passed date
 * @returns
 */
export const formatDate = (
  date,
  dateFormat = 'MM/dd/yyyy',
  truncateTimezone = true
) => {
  const type = typeof date;

  switch (type) {
    case 'object': {
      if (date === null || !isValid(date)) return '';
      return format(date, dateFormat);
    }
    case 'bigint':
    case 'number':
    case 'string': {
      let parsedDate;

      parsedDate = new Date(date);

      if (!isValid(parsedDate)) {
        // Fallback for ISO strings
        parsedDate = parseISO(date);
      }

      if (!isValid(parsedDate)) return '';

      if (truncateTimezone && typeof date === 'string' && date.includes('-')) {
        const newDateNoTimezone = new Date(
          date.slice(0, 10).replace(/-/g, '/')
        );
        if (isValid(newDateNoTimezone)) {
          return format(newDateNoTimezone, dateFormat);
        }
      }

      return format(parsedDate, dateFormat);
    }
    case 'boolean':
    case 'undefined':
    case 'function':
    case 'symbol':
    default:
      console.warn(
        `Unsupported date type supplied to formatDate function: ${type}`
      );
      return '';
  }
};

/**
 * Gets the truncates the time and timezone safely off a date string.
 */
export const getDatePart = date => {
  if (!date) return null;
  return date.slice(0, 10);
};

//Checks to see if a date string that it is valid
//If it isn't, return null instead
export function nullDateIfNotValid(date) {
  return date && !isSameDay(parseISO(date.slice(0, 10)), parseISO(END_OF_DAYS))
    ? date
    : null;
}

/**
 * Calendar date only (no time / timezone shift) for worklist start/end payloads.
 * Accepts a Date or ISO-ish string; returns YYYY-MM-DD in the user's local calendar.
 */
export function toLocalDateOnlyString(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string') {
    const head = value.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(head)) return head;
  }
  const d = value instanceof Date ? value : new Date(value);
  if (!isValid(d)) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

//Creates the default start and end dates for the analytics pages
//Added in timeframeInMonths to allow for different timeframes if that ever comes up
//Default is 6 months
export function getDefaultAnalyticsDates(timeframeInMonths = 6) {
  const startDateObj = new Date();
  startDateObj.setDate(1);
  startDateObj.setMonth(startDateObj.getMonth() - (timeframeInMonths + 1));
  const defaultStartDate = startDateObj.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  const defaultEndDate = new Date().toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });

  return { defaultStartDate, defaultEndDate };
}

/**
 * Calculate a minimum start date that is N months ago from today
 * @param {number} monthsAgo - Number of months to go back (default: 13)
 * @returns {string} Formatted date string in MM/DD/YYYY format
 */
export function getMinStartDate(monthsAgo = 13) {
  const date = new Date();
  date.setDate(1);
  date.setMonth(date.getMonth() - monthsAgo);
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}
